import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getFights, createFight, updateFight, deleteFight, getFighters, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { format, parseISO, isFuture } from 'date-fns';
import { 
  Plus, 
  Swords,
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  MapPin,
  Calendar as CalendarIcon,
  Trophy,
  X,
  Check,
  Dumbbell,
  Medal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn } from '../lib/utils';

const WEIGHT_CLASSES = [
  'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight', 
  'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight', 'Heavyweight'
];

const BJJ_WEIGHT_CLASSES = [
  'Rooster', 'Light Feather', 'Feather', 'Light', 'Middle', 
  'Medium Heavy', 'Heavy', 'Super Heavy', 'Ultra Heavy', 'Absolute'
];

const BJJ_DIVISIONS = [
  'Juvenile', 'Adult', 'Master 1', 'Master 2', 'Master 3', 
  'Master 4', 'Master 5', 'Master 6', 'Master 7'
];

const RESULT_OPTIONS = ['Win', 'Loss', 'Draw', 'No Contest'];
const BJJ_RESULT_OPTIONS = ['Gold', 'Silver', 'Bronze', 'No Medal'];
const METHOD_OPTIONS = ['KO/TKO', 'Submission', 'Decision', 'Split Decision', 'Unanimous Decision', 'DQ'];
const BJJ_METHOD_OPTIONS = ['Submission', 'Points', 'Advantage', 'Referee Decision', 'DQ'];

// Fights List Page
export const FightsListPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterFighterId = searchParams.get('fighter');
  
  const [fights, setFights] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSport, setFilterSport] = useState('all'); // 'all', 'mma', 'bjj'
  const [resultDialog, setResultDialog] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
    trackFeatureUsage('fights', 'view').catch(() => {});
  }, [filterFighterId]);

  const loadData = async () => {
    try {
      const [fightsRes, fightersRes] = await Promise.all([
        getFights(filterFighterId),
        getFighters()
      ]);
      setFights(fightsRes.data);
      setFighters(fightersRes.data);
    } catch (error) {
      toast.error('Failed to load fights');
    } finally {
      setLoading(false);
    }
  };

  const getFighterName = (fighterId) => {
    const fighter = fighters.find(f => f.id === fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const filteredFights = fights.filter(fight => {
    // Filter by status
    if (filterType === 'upcoming' && (fight.result || !isFuture(parseISO(fight.fight_date)))) return false;
    if (filterType === 'completed' && !fight.result) return false;
    // Filter by sport type
    if (filterSport !== 'all' && fight.sport_type !== filterSport) return false;
    return true;
  });

  const handleUpdateResult = async (result, method) => {
    if (!resultDialog) return;
    try {
      await updateFight(resultDialog.id, { result, result_method: method });
      toast.success('Fight result updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update result');
    } finally {
      setResultDialog(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteFight(deleteId);
      toast.success('Fight deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete fight');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fights-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Events & Competitions
            </h1>
            <p className="text-zinc-400 mt-1">
              {filteredFights.length} {filterSport === 'bjj' ? 'tournaments' : filterSport === 'mma' ? 'fights' : 'events'} scheduled
            </p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => navigate('/fights/new')}
              className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold tracking-wide"
              data-testid="add-fight-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 h-12 bg-zinc-900 border-zinc-800" data-testid="fight-type-filter">
              <SelectValue placeholder="Filter events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFilterSport('all')}
              className={`px-4 py-2 rounded-sm border transition-all ${
                filterSport === 'all'
                  ? 'border-red-600 bg-red-600/10 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              All Sports
            </button>
            <button
              onClick={() => setFilterSport('mma')}
              className={`px-4 py-2 rounded-sm border transition-all flex items-center gap-2 ${
                filterSport === 'mma'
                  ? 'border-red-600 bg-red-600/10 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              <Swords className="w-4 h-4" />
              MMA Fights
            </button>
            <button
              onClick={() => setFilterSport('bjj')}
              className={`px-4 py-2 rounded-sm border transition-all flex items-center gap-2 ${
                filterSport === 'bjj'
                  ? 'border-blue-600 bg-blue-600/10 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              <Dumbbell className="w-4 h-4" />
              BJJ Tournaments
            </button>
          </div>
        </div>

        {/* Fights List */}
        {filteredFights.length > 0 ? (
          <div className="space-y-4">
            {filteredFights.map((fight, index) => {
              const isUpcoming = !fight.result && isFuture(parseISO(fight.fight_date));
              const isBJJ = fight.sport_type === 'bjj';
              return (
                <motion.div
                  key={fight.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={cn(
                    "p-6 bg-zinc-900/50 border rounded-sm",
                    isBJJ && fight.result === 'Gold' && "border-yellow-500/50",
                    isBJJ && fight.result === 'Silver' && "border-gray-400/50",
                    isBJJ && fight.result === 'Bronze' && "border-amber-600/50",
                    !isBJJ && fight.result === 'Win' && "border-green-600/50",
                    !isBJJ && fight.result === 'Loss' && "border-red-600/50",
                    !fight.result && "border-zinc-800"
                  )}
                  data-testid={`fight-card-${fight.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Fight Info */}
                    <div className="flex-1">
                      {/* Sport Type Badge */}
                      <div className="flex items-center gap-2 mb-2">
                        {isBJJ ? (
                          <span className="px-2 py-0.5 text-xs uppercase font-medium rounded-sm border bg-blue-600/20 text-blue-500 border-blue-600/30 flex items-center gap-1">
                            <Dumbbell className="w-3 h-3" />
                            BJJ {fight.gi_nogi === 'nogi' ? 'No-Gi' : fight.gi_nogi === 'gi' ? 'Gi' : ''}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs uppercase font-medium rounded-sm border bg-red-600/20 text-red-500 border-red-600/30 flex items-center gap-1">
                            <Swords className="w-3 h-3" />
                            MMA
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mb-3">
                        <Link 
                          to={`/fighters/${fight.fighter_id}`}
                          className="text-lg text-white font-medium hover:text-red-500"
                        >
                          {getFighterName(fight.fighter_id)}
                        </Link>
                        {!isBJJ && (
                          <>
                            <span className="text-zinc-500">vs</span>
                            <span className="text-lg text-zinc-300">{fight.opponent_name}</span>
                          </>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {format(parseISO(fight.fight_date), 'M/d/yyyy')}
                        </span>
                        <span className="px-2 py-0.5 bg-zinc-800 rounded-sm text-xs">
                          {fight.weight_class}
                        </span>
                        {isBJJ ? (
                          <>
                            {fight.division && <span>{fight.division}</span>}
                          </>
                        ) : (
                          <span>{fight.rounds} rounds</span>
                        )}
                        {fight.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {fight.location}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-zinc-300 mt-2">{fight.event_name}</p>
                    </div>

                    {/* Result / Actions */}
                    <div className="flex items-center gap-4">
                      {fight.result ? (
                        <div className={cn(
                          "px-4 py-2 rounded-sm text-center min-w-[100px]",
                          isBJJ && fight.result === 'Gold' && "bg-yellow-500/20 text-yellow-500",
                          isBJJ && fight.result === 'Silver' && "bg-gray-400/20 text-gray-300",
                          isBJJ && fight.result === 'Bronze' && "bg-amber-600/20 text-amber-500",
                          isBJJ && fight.result === 'No Medal' && "bg-zinc-600/20 text-zinc-400",
                          !isBJJ && fight.result === 'Win' && "bg-green-600/20 text-green-500",
                          !isBJJ && fight.result === 'Loss' && "bg-red-600/20 text-red-500",
                          !isBJJ && fight.result === 'Draw' && "bg-zinc-600/20 text-zinc-400",
                          !isBJJ && fight.result === 'No Contest' && "bg-yellow-600/20 text-yellow-500"
                        )}>
                          <p className="font-heading font-bold uppercase flex items-center justify-center gap-1">
                            {isBJJ && fight.result === 'Gold' && <Medal className="w-4 h-4" />}
                            {isBJJ && fight.result === 'Silver' && <Medal className="w-4 h-4" />}
                            {isBJJ && fight.result === 'Bronze' && <Medal className="w-4 h-4" />}
                            {fight.result}
                          </p>
                          {fight.result_method && (
                            <p className="text-xs opacity-75">{fight.result_method}</p>
                          )}
                        </div>
                      ) : (
                        <div className="px-4 py-2 bg-zinc-800 rounded-sm text-center">
                          <p className="text-zinc-400 text-sm uppercase">
                            {isUpcoming ? 'Upcoming' : 'Pending Result'}
                          </p>
                        </div>
                      )}

                      {isAdmin && (
                        <div className="flex gap-2">
                          {!fight.result && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setResultDialog(fight)}
                              className="text-zinc-400 hover:text-green-500"
                              data-testid={`record-result-${fight.id}`}
                            >
                              <Trophy className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/fights/${fight.id}/edit`)}
                            className="text-zinc-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteId(fight.id)}
                            className="text-zinc-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {fight.notes && (
                    <p className="text-zinc-500 text-sm mt-4 pt-4 border-t border-zinc-800">
                      {fight.notes}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <Swords className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-lg mb-2">No fights found</p>
            {isAdmin && (
              <Button 
                onClick={() => navigate('/fights/new')}
                className="mt-4 bg-red-600 hover:bg-red-700"
              >
                Schedule a fight
              </Button>
            )}
          </div>
        )}

        {/* Record Result Dialog */}
        <ResultDialog 
          fight={resultDialog}
          onClose={() => setResultDialog(null)}
          onSave={handleUpdateResult}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Fight</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete this fight? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

// Result Dialog Component
const ResultDialog = ({ fight, onClose, onSave }) => {
  const [result, setResult] = useState('');
  const [method, setMethod] = useState('');

  if (!fight) return null;

  return (
    <Dialog open={!!fight} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white">Record Fight Result</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Result</Label>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="Select result" />
              </SelectTrigger>
              <SelectContent>
                {RESULT_OPTIONS.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                {METHOD_OPTIONS.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button 
            onClick={() => onSave(result, method)}
            disabled={!result}
            className="bg-red-600 hover:bg-red-700"
          >
            Save Result
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Fight Form Page
export const FightFormPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEdit = window.location.pathname.includes('/edit');
  const fightId = isEdit ? window.location.pathname.split('/')[2] : null;
  
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sportType, setSportType] = useState('mma'); // 'mma' or 'bjj'
  const [formData, setFormData] = useState({
    fighter_id: '',
    opponent_name: '',
    event_name: '',
    fight_date: '',
    location: '',
    weight_class: '',
    rounds: 3,
    notes: '',
    // BJJ-specific fields
    division: '',
    gi_nogi: 'gi'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const fightersRes = await getFighters();
      setFighters(fightersRes.data);
      
      if (isEdit && fightId) {
        const fightsRes = await getFights();
        const fight = fightsRes.data.find(f => f.id === fightId);
        if (fight) {
          setSportType(fight.sport_type || 'mma');
          setFormData({
            fighter_id: fight.fighter_id,
            opponent_name: fight.opponent_name || '',
            event_name: fight.event_name,
            fight_date: fight.fight_date,
            location: fight.location || '',
            weight_class: fight.weight_class,
            rounds: fight.rounds || 3,
            division: fight.division || '',
            gi_nogi: fight.gi_nogi || 'gi',
            notes: fight.notes || ''
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        sport_type: sportType,
        // Clear irrelevant fields based on sport type
        rounds: sportType === 'bjj' ? null : formData.rounds,
        opponent_name: sportType === 'bjj' ? null : formData.opponent_name,
        division: sportType === 'bjj' ? formData.division : null,
        gi_nogi: sportType === 'bjj' ? formData.gi_nogi : null
      };
      
      if (isEdit) {
        await updateFight(fightId, submitData);
        toast.success(sportType === 'bjj' ? 'Tournament updated' : 'Fight updated');
      } else {
        await createFight(submitData);
        toast.success(sportType === 'bjj' ? 'Tournament scheduled' : 'Fight scheduled');
      }
      navigate('/fights');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    navigate('/fights');
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="fight-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/fights')}
          className="text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fights
        </Button>

        <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-8">
          {isEdit ? (sportType === 'bjj' ? 'Edit Tournament' : 'Edit Fight') : (sportType === 'bjj' ? 'Schedule Tournament' : 'Schedule Fight')}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sport Type Toggle */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Event Type *</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setSportType('mma')}
                className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                  sportType === 'mma'
                    ? 'border-red-600 bg-red-600/10 text-white'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
                data-testid="event-type-mma"
              >
                <Swords className="w-5 h-5" />
                MMA Fight
              </button>
              <button
                type="button"
                onClick={() => setSportType('bjj')}
                className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                  sportType === 'bjj'
                    ? 'border-blue-600 bg-blue-600/10 text-white'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
                data-testid="event-type-bjj"
              >
                <Dumbbell className="w-5 h-5" />
                BJJ Tournament
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-zinc-300">Fighter *</Label>
              <Select 
                value={formData.fighter_id} 
                onValueChange={(value) => {
                  const fighter = fighters.find(f => f.id === value);
                  setFormData({ 
                    ...formData, 
                    fighter_id: value,
                    weight_class: fighter?.weight_class || formData.weight_class
                  });
                }}
              >
                <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fight-fighter-select">
                  <SelectValue placeholder="Select fighter" />
                </SelectTrigger>
                <SelectContent>
                  {fighters.map(fighter => (
                    <SelectItem key={fighter.id} value={fighter.id}>
                      {fighter.name} - {fighter.weight_class}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {sportType === 'mma' ? (
              <div className="space-y-2">
                <Label htmlFor="opponent" className="text-zinc-300">Opponent Name *</Label>
                <Input
                  id="opponent"
                  value={formData.opponent_name}
                  onChange={(e) => setFormData({ ...formData, opponent_name: e.target.value })}
                  placeholder="John Doe"
                  className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                  required={sportType === 'mma'}
                  data-testid="fight-opponent-input"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-zinc-300">Gi / No-Gi *</Label>
                <Select 
                  value={formData.gi_nogi} 
                  onValueChange={(value) => setFormData({ ...formData, gi_nogi: value })}
                >
                  <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fight-gi-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gi">Gi</SelectItem>
                    <SelectItem value="nogi">No-Gi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="event" className="text-zinc-300">{sportType === 'bjj' ? 'Tournament Name *' : 'Event Name *'}</Label>
            <Input
              id="event"
              value={formData.event_name}
              onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
              placeholder={sportType === 'bjj' ? 'IBJJF World Championship' : 'UFC 300'}
              className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
              required
              data-testid="fight-event-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-zinc-300">Fight Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-12 justify-start text-left font-normal bg-zinc-900 border-zinc-800",
                      !formData.fight_date && "text-zinc-500"
                    )}
                    data-testid="fight-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.fight_date ? format(parseISO(formData.fight_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                  <Calendar
                    mode="single"
                    selected={formData.fight_date ? parseISO(formData.fight_date) : undefined}
                    onSelect={(date) => setFormData({ ...formData, fight_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-zinc-300">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Las Vegas, NV"
                className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                data-testid="fight-location-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-zinc-300">Weight Class *</Label>
              <Select 
                value={formData.weight_class} 
                onValueChange={(value) => setFormData({ ...formData, weight_class: value })}
              >
                <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fight-weight-class-select">
                  <SelectValue placeholder="Select weight class" />
                </SelectTrigger>
                <SelectContent>
                  {(sportType === 'bjj' ? BJJ_WEIGHT_CLASSES : WEIGHT_CLASSES).map(wc => (
                    <SelectItem key={wc} value={wc}>{wc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {sportType === 'mma' ? (
              <div className="space-y-2">
                <Label className="text-zinc-300">Rounds</Label>
                <Select 
                  value={String(formData.rounds)} 
                  onValueChange={(value) => setFormData({ ...formData, rounds: parseInt(value) })}
                >
                  <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fight-rounds-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Rounds</SelectItem>
                    <SelectItem value="5">5 Rounds (Championship)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-zinc-300">Division</Label>
                <Select 
                  value={formData.division} 
                  onValueChange={(value) => setFormData({ ...formData, division: value })}
                >
                  <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fight-division-select">
                    <SelectValue placeholder="Select division" />
                  </SelectTrigger>
                  <SelectContent>
                    {BJJ_DIVISIONS.map(div => (
                      <SelectItem key={div} value={div}>{div}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-zinc-300">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={sportType === 'bjj' ? 'Additional notes about the tournament...' : 'Additional notes about the fight...'}
              className="min-h-[100px] bg-zinc-900 border-zinc-800 focus:border-red-600"
              data-testid="fight-notes-input"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/fights')}
              className="flex-1 h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.fighter_id || !formData.event_name || !formData.fight_date || !formData.weight_class || (sportType === 'mma' && !formData.opponent_name)}
              className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
              data-testid="save-fight-btn"
            >
              {saving ? 'Saving...' : isEdit ? (sportType === 'bjj' ? 'Update Tournament' : 'Update Fight') : (sportType === 'bjj' ? 'Schedule Tournament' : 'Schedule Fight')}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};
